import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Building2 } from "lucide-react";
import AddBranchModal from "../components/admin/branch/AddBranchModal";
import ConfirmationModal from "../components/CommonComponents/ConfirmationModal";
import EmptyState from "../components/CommonComponents/EmptyState";
import Pagination from "../components/CommonComponents/Pagination";
import PrimaryButton from "../components/CommonComponents/PrimaryButton";
import SearchInput from "../components/CommonComponents/SearchInput";
import TableActionButtons from "../components/CommonComponents/TableActionButtons";
import { tableBase, tableCell, tableContainer, tableHead, tableHeader, tableRow } from "../utils/tableStyle";
import { deleteBranch, editBranch, fetchBranches } from "../services/branchApi";
import { showErrorToast, showSuccessToast } from "../utils/toast";

export default function CustomerBranchesModal({ customer, isOpen, onClose }) {
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [modalState, setModalState] = useState({ type: null, branch: null, open: false });
  const branchesPerPage = 5;

  const loadBranches = useCallback(async () => {
    if (!customer?.id) return;
    setLoading(true);
    try {
      const response = await fetchBranches(customer.id);
      setBranches(response.data?.data || response.data || []);
    } catch (error) {
      console.error("Failed to load customer branches:", error);
      showErrorToast("Failed to load branches.");
    } finally {
      setLoading(false);
    }
  }, [customer?.id]);

  useEffect(() => {
    if (isOpen) {
      setSearchTerm("");
      setCurrentPage(1);
      loadBranches();
    }
  }, [isOpen, loadBranches]);

  const filteredBranches = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    if (!query) return branches;
    return branches.filter((branch) =>
      `${branch.name || ""} ${branch.location || ""}`.toLowerCase().includes(query)
    );
  }, [branches, searchTerm]);

  const totalPages = Math.max(Math.ceil(filteredBranches.length / branchesPerPage), 1);
  const currentBranches = filteredBranches.slice(
    (currentPage - 1) * branchesPerPage,
    currentPage * branchesPerPage
  );

  const closeInnerModal = () => setModalState({ type: null, branch: null, open: false });

  const handleAfterBranchSave = () => {
    closeInnerModal();
    loadBranches();
  };

  const confirmDelete = async () => {
    if (!modalState.branch) return;
    try {
      await deleteBranch(modalState.branch.id, customer.id);
      showSuccessToast("Branch deleted successfully.");
      loadBranches();
    } catch (error) {
      showErrorToast("Failed to delete branch.");
    } finally {
      closeInnerModal();
    }
  };

  const confirmToggle = async () => {
    if (!modalState.branch) return;
    const nextStatus = modalState.branch.status === "ACTIVE" ? "INACTIVE" : "ACTIVE";
    try {
      await editBranch(modalState.branch.id, {
        ...modalState.branch,
        customer_id: customer.id,
        status: nextStatus,
      });
      showSuccessToast(`Branch ${nextStatus === "ACTIVE" ? "activated" : "deactivated"} successfully.`);
      loadBranches();
    } catch (error) {
      showErrorToast("Failed to update branch status.");
    } finally {
      closeInnerModal();
    }
  };

  if (!isOpen || !customer) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
      <div className="flex max-h-[88vh] w-full max-w-5xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
        <div className="flex items-start justify-between gap-4 border-b border-slate-200 px-6 py-4">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600">
              <Building2 className="h-3.5 w-3.5" />
              Branch Management
            </div>
            <h2 className="mt-3 text-xl font-semibold text-slate-900">{customer.name}</h2>
            <p className="mt-1 text-sm text-slate-500">Manage branches for this customer account.</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full border border-slate-200 px-3 py-1.5 text-sm font-semibold text-slate-600 transition hover:bg-slate-50"
          >
            Close
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5">
          <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <SearchInput
              value={searchTerm}
              onChange={(event) => {
                setSearchTerm(event.target.value);
                setCurrentPage(1);
              }}
              placeholder="Search branch name or location..."
            />
            <PrimaryButton onClick={() => setModalState({ type: "add", branch: null, open: true })}>
              + Add Branch
            </PrimaryButton>
          </div>

          {loading ? (
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-8 text-center text-sm text-slate-500">
              Loading branches...
            </div>
          ) : (
            <div className={tableContainer}>
              <table className={tableBase}>
                <thead className={tableHead} style={{ background: "var(--color-primary)" }}>
                  <tr>
                    <th className={tableHeader + " text-left"}>Name</th>
                    <th className={tableHeader + " text-left"}>Location</th>
                    <th className={tableHeader + " text-left"}>Status</th>
                    <th className={tableHeader + " text-center"}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {currentBranches.length === 0 ? (
                    <tr>
                      <td colSpan={4} className={tableCell + " text-center py-8"}>
                        <EmptyState icon={Building2} message="No branches found." size="default" />
                      </td>
                    </tr>
                  ) : (
                    currentBranches.map((branch) => (
                      <tr key={branch.id} className={tableRow + " border-b last:border-0"}>
                        <td className={tableCell}>{branch.name}</td>
                        <td className={tableCell}>{branch.location || <span className="text-gray-400">-</span>}</td>
                        <td className={tableCell}>
                          <span className={`rounded px-2 py-1 text-xs font-medium ${branch.status === "ACTIVE" ? "bg-green-100 text-green-700" : branch.status === "DELETED" ? "bg-red-100 text-red-600" : "bg-gray-200 text-gray-500"}`}>
                            {branch.status}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-center align-middle">
                          {String(branch.status).toUpperCase() === "DELETED" ? (
                            <span className="text-sm text-gray-400">-</span>
                          ) : (
                            <TableActionButtons
                              canEdit
                              canDelete
                              canToggle
                              toggleStatus={branch.status}
                              onEdit={() => setModalState({ type: "edit", branch, open: true })}
                              onDelete={() => setModalState({ type: "delete", branch, open: true })}
                              onToggle={() => setModalState({ type: "toggle", branch, open: true })}
                            />
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}

          <div className="mt-4 flex flex-col items-center gap-2">
            <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
            <p className="text-sm text-slate-500">
              Showing {filteredBranches.length === 0 ? 0 : (currentPage - 1) * branchesPerPage + 1}
              {" - "}
              {Math.min(currentPage * branchesPerPage, filteredBranches.length)}
              {" of "}
              {filteredBranches.length} branches
            </p>
          </div>
        </div>
      </div>

      {modalState.open && (modalState.type === "add" || modalState.type === "edit") && (
        <AddBranchModal
          isOpen
          onClose={closeInnerModal}
          onBranchAdded={handleAfterBranchSave}
          initialData={modalState.type === "edit" ? modalState.branch : null}
          isEditMode={modalState.type === "edit"}
          customerId={customer.id}
        />
      )}

      <ConfirmationModal
        isOpen={modalState.open && modalState.type === "delete"}
        onClose={closeInnerModal}
        onConfirm={confirmDelete}
        title="Delete Branch"
        message={`Are you sure you want to delete "${modalState.branch?.name}"?`}
        confirmText="Yes"
        cancelText="No"
        type="delete"
      />

      <ConfirmationModal
        isOpen={modalState.open && modalState.type === "toggle"}
        onClose={closeInnerModal}
        onConfirm={confirmToggle}
        title="Change Branch Status"
        message={`Are you sure you want to set this branch to ${modalState.branch?.status === "ACTIVE" ? "INACTIVE" : "ACTIVE"}?`}
        confirmText="Yes"
        cancelText="No"
        type="delete"
      />
    </div>
  );
}
